import { useEffect, useState } from 'react';
import { Button, Collapse, Dropdown, TabList, css } from '@appd/uik';
import { QueryBuilder } from './QueryBuilder';
import { entries, isEqual, keys } from 'lodash';
import { Datasource, ExpressionJson } from '../../../../models';
import { ASSISTED_QUERY_BUILDER, QUERY_BOX } from './DataPanelConstants';
import { TransformedDataSets } from './TransformedDataSets';

interface DataPanelProps {
  filters: any; // hash of: global filter var
  datasources: Record<string, Datasource>; // hash of: data source type: uql, query obj or string
  expressionJson: Record<string, ExpressionJson>;
  onInspect: (queryKey: string) => void;
  onSave: (dataSources: Record<string, Datasource>) => void;
}

export const DataPanel = ({ filters, datasources, expressionJson, onInspect, onSave }: DataPanelProps): JSX.Element => {
  const [dataSources, setDataSources] = useState(
    entries(datasources).map(([key, value]) => ({ key: key, value: value, uuid: crypto.randomUUID() })),
  );
  const [dataPanelKey, setDataPanelKey] = useState('Data-Panel-' + Date.now());
  const [schemaJson, setSchemaJson] = useState(null);
  const [tabSelected, setTabSelected] = useState(0);

  useEffect(() => {
    setDataSources(entries(datasources).map(([key, value]) => ({ key: key, value: value, uuid: crypto.randomUUID() })));
  }, [datasources]);

  useEffect(() => {
    const layerId = sessionStorage.getItem('appd-next-tenant-id') ?? '47a01df9-54a0-472b-96b8-7c8f64eb7cbf';
    const getSchemaRequest = new Request('/session/api/ext/monitoring/v1beta2/types/entity', {
      headers: {
        Accept: 'application/json; charset=UTF-8',
        'layer-type': 'TENANT',
        'layer-id': layerId,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    fetch(getSchemaRequest)
      .then(res => {
        if (!res.ok) {
          throw new Error('Get Schema Failed');
        }
        return res.json();
      })
      .then(data => {
        setSchemaJson(data);
      })
      .catch(err => {
        console.error('Error: ', err);
      });
  }, []);

  const reduceDataSources = (
    ds: {
      key: string;
      value: Datasource;
      uuid: string;
    }[],
  ) => {
    const res: Record<string, Datasource> = ds.reduce<Record<string, Datasource>>((accumulator, { key, value }) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
    return res;
  };

  const onSaveData = () => {
    onSave(reduceDataSources(dataSources));
  };

  const onAddQuery = (key: number) => {
    const newIndex = dataSources.length;
    let newKey = 'New Query ' + newIndex;
    while (keys(dataSources).includes(newKey)) {
      newKey += newIndex;
    }
    const newContent = {
      type: 'uql',
      options: {
        since: '/${ globals.variables.since }',
        until: '/${ globals.variables.until }',
        filters: ['isActive = true'],
        editorMode: key == ASSISTED_QUERY_BUILDER ? 'builder' : 'query',
        fromEntities: [],
        fetchItems: {},
        from: "${ 'FROM entities(' & $join([fromEntities], ',') & ')' }",
        fetch: "${ 'FETCH ' & $join($each(fetchItems, function($v, $k) { '`' & $k & '`' & ':' & $v }), ', ') }",
        filterStr: '/${ globals.filterStr }',
        limits: [],
        order: '',
        limitStr:
          "${ $count([$filter(limits, $boolean)]) > 0 ? 'LIMITS ' & $join([$filter(limits, $boolean)], ' ') : '' }",
        rawStr: '',
        queryStr: "${ editorMode != 'query' ? $join([from, filterStr, fetch, order, limitStr], ' ') : rawStr }",
      },
    } as Datasource;
    setDataSources([
      ...dataSources,
      {
        key: newKey,
        value: newContent,
        uuid: crypto.randomUUID(),
      },
    ]);
  };

  const onQueryChanged = (index: number, name: string, query: Datasource) => {
    const oldName = dataSources[index].key;
    let newDataSources = [...dataSources];
    if (!isEqual(oldName, name)) {
      newDataSources.forEach((e, i) => {
        if (e.key == oldName) {
          newDataSources[i].key = name;
        }
      });
    } else if (!isEqual(dataSources[index].value, query)) {
      newDataSources.forEach((e, i) => {
        if (e.key == oldName) {
          newDataSources[i].value = query;
        }
      });
    }
    setDataSources(newDataSources);
  };

  const onDelete = (key: string) => {
    let newDataSources = [...dataSources];
    newDataSources.splice(
      newDataSources.findIndex(x => x.key == key),
      1,
    );
    setDataSources(newDataSources);
  };

  const tabList = [
    {
      value: 'Queries',
      content: (
        <div className="mt-2">
          <Collapse.Group open={[true]}>
            {dataSources.map((ds, i) => (
              <QueryBuilder
                key={dataPanelKey + 'ChildQueryBuilder' + ds.uuid}
                index={i}
                name={ds.key}
                schema={schemaJson}
                dataSource={ds.value}
                onChanged={onQueryChanged}
                onDelete={onDelete}
                onInspect={onInspect}
              ></QueryBuilder>
            ))}
          </Collapse.Group>
          <div className="flex flex-row items-center justify-between mt-2">
            <Dropdown kind="PRIMARY" icon="Action.Add" label="Add Query">
              <Dropdown.Option key={0} onClick={() => onAddQuery(ASSISTED_QUERY_BUILDER)}>
                UI
              </Dropdown.Option>
              <Dropdown.Option key={1} onClick={() => onAddQuery(QUERY_BOX)}>
                Query
              </Dropdown.Option>
            </Dropdown>
          </div>
          <div className="flex flex-row justify-end mb-2">
            <Button onClick={onSaveData}>Apply</Button>
          </div>
        </div>
      ),
    },
    {
      value: 'Data Sets',
      content: (
        <div className="mt-2">
          <TransformedDataSets expressionJson={expressionJson}></TransformedDataSets>
        </div>
      ),
    },
  ];

  if (!schemaJson) {
    return <div></div>;
  }

  return (
    <div className="editor-data-panel flex flex-col flex-1 p-5 py-2 bg-default-bg-weak h-full">
      <div className="title-part flex flex-row items-center justify-between mb-2">
        <div className="left-part text-2xl font-bold">Data</div>
      </div>
      <TabList
        className={css({ minHeight: 45 })}
        kind="PRIMARY"
        selected={tabSelected}
        onSelectedChange={(index: number) => setTabSelected(index)}
      >
        {tabList.map(({ value }) => (
          <div key={value}>{value}</div>
        ))}
      </TabList>
      <div>{tabList[tabSelected].content}</div>
    </div>
  );
};
